import useSurveyState from "@hooks/useSurveyState";
import useDidUpdateEffect from "@shared/hooks/useDidUpdateEffect";
import { updateCurrentSurvey } from "@survey/surveySlice";
import { useRef, useState } from "react";

interface UseSurveyUpdateOptions<T> {
  transformBeforeSave?: (value: T) => T;
  onSaveError?: (error: unknown, originalValue: T) => void;
}

/**
 * Hook for managing survey field updates with onChange/onBlur pattern.
 * - onChange: Updates local state and Redux immediately
 * - Manual trigger: Call triggerSave() on blur to persist to API only if changed
 */
const useSurveyUpdate = <T extends string | number | boolean>(
  fieldName: string,
  initialValue: T,
  options: UseSurveyUpdateOptions<T> = {},
) => {
  const { transformBeforeSave, onSaveError } = options;
  const { survey, dispatch, updateSurvey } = useSurveyState();

  const [localValue, setLocalValue] = useState<T>(initialValue);
  const originalValue = useRef<T>(initialValue);

  // Update local state and original reference when survey data changes
  useDidUpdateEffect(() => {
    if (survey) {
      const currentValue = (survey as unknown as Record<string, unknown>)[
        fieldName
      ] as T;
      if (currentValue !== undefined) {
        setLocalValue(currentValue);
        originalValue.current = currentValue;
      }
    }
  }, [survey?.id]);

  // Handle onChange - update local state and Redux immediately
  const setValue = (value: T) => {
    if (!survey) return;

    setLocalValue(value);
    dispatch(updateCurrentSurvey({ [fieldName]: value }));
  };

  // Handle onBlur - trigger API call only if value changed
  const triggerSave = () => {
    if (!survey) return;

    let trimmedValue =
      typeof localValue === "string"
        ? ((localValue as string).trim() as T)
        : localValue;

    // Apply custom transformation if provided
    if (transformBeforeSave) {
      trimmedValue = transformBeforeSave(trimmedValue);
      setLocalValue(trimmedValue); // Update local state with transformed value
    }

    // Only make API call if value actually changed
    if (trimmedValue !== originalValue.current) {
      updateSurvey({
        id: survey.id,
        [fieldName]: trimmedValue,
      })
        .unwrap()
        .then(() => {
          // Update original value reference on success
          originalValue.current = trimmedValue;
        })
        .catch((error: unknown) => {
          if (onSaveError) {
            onSaveError(error, originalValue.current);
          } else {
            console.error("Failed to update survey field");
          }
        });
    }
  };

  return {
    value: localValue,
    setValue,
    triggerSave,
  };
};

export default useSurveyUpdate;
